import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const emptyForm = { name: "", email: "", password: "" };

export default function AgencyUsersModal({ agency, onClose }) {
  const nav = useNavigate();
  const { impersonateAgency } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [pwFor, setPwFor] = useState(null); // { id, email }
  const [pwValue, setPwValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/agencies/${agency.id}/users`);
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento utenti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [agency.id]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8) {
      toast.error("Compila nome, email e password (min. 8 caratteri)");
      return;
    }
    try {
      await api.post(`/agencies/${agency.id}/users`, form);
      toast.success("Agente creato");
      setForm(emptyForm); setCreating(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore creazione"); }
  };

  const startEdit = (u) => { setEditingId(u.id); setEditForm({ name: u.name || "", email: u.email }); };
  const saveEdit = async () => {
    try {
      await api.patch(`/agencies/${agency.id}/users/${editingId}`, editForm);
      toast.success("Aggiornato");
      setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore aggiornamento"); }
  };

  const resetPassword = async () => {
    if (pwValue.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    try {
      await api.post(`/agencies/${agency.id}/users/${pwFor.id}/password`, { password: pwValue });
      toast.success(`Password reimpostata per ${pwFor.email}`);
      setPwFor(null); setPwValue("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore reset password"); }
  };

  const toggleActive = async (u) => {
    const action = u.active === false ? "activate" : "deactivate";
    try {
      await api.post(`/agencies/${agency.id}/users/${u.id}/${action}`);
      toast.success(u.active === false ? "Agente riattivato" : "Agente disattivato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
  };

  const del = async (u) => {
    if (!window.confirm(`Rimuovere definitivamente l'accesso di "${u.name}" (${u.email})?\n\nLo storico degli immobili, lead e verifiche resta intatto: viene cancellato SOLO il login. Per conservare lo storico dell'agente è preferibile "Disattiva".`)) return;
    try {
      await api.delete(`/agencies/${agency.id}/users/${u.id}`);
      toast.success("Agente rimosso");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore rimozione"); }
  };

  const impersonate = async (u) => {
    if (u.active === false) { toast.error("Agente disattivato — riattivalo prima di impersonarlo"); return; }
    const r = await impersonateAgency(agency.id, u.id);
    if (!r.ok) { toast.error(r.error || "Impossibile avviare la sessione"); return; }
    toast.success(`Ora stai visualizzando come ${u.name}`);
    onClose();
    // replace: sostituisce la history admin così la freccia "indietro" browser
    // non torna nel pannello admin (che ora sarebbe agency-scoped, quindi vuoto).
    nav("/area-agenzie", { replace: true });
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4"
         data-testid="ag-users-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white border border-[#E6E4E0] max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="px-8 py-5 border-b border-[#E6E4E0] flex justify-between items-start">
          <div>
            <div className="label-uppercase text-[#7D2E3F]">Gestione accessi Area Agenzie</div>
            <h2 className="font-serif text-2xl mt-1">{agency.name}</h2>
            <div className="text-xs text-[#5C5657] mt-2 max-w-lg">
              Gli agenti sono <strong>credenziali di accesso</strong> all&apos;Area Agenzie. Tutti gli agenti dell&apos;agenzia vedono in condivisione i medesimi immobili. I lead vanno all&apos;email agenzia (<strong>{agency.email || "non impostata"}</strong>), non alle email degli agenti.
            </div>
          </div>
          <button onClick={onClose} data-testid="ag-users-close" className="label-uppercase text-xs text-[#5C5657]">Chiudi ×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-6">
          {loading ? (
            <div className="text-sm text-[#5C5657]">Caricamento…</div>
          ) : (
            <>
              <table className="admin-table w-full bg-white border border-[#E6E4E0]">
                <thead><tr><th>Nome</th><th>Email di login</th><th>Stato</th><th className="text-right">Azioni</th></tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`ag-user-${u.id}`} className={u.active === false ? "opacity-60" : ""}>
                      <td>
                        {editingId === u.id ? (
                          <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                 className="admin-input" />
                        ) : (
                          <span className="font-medium" data-testid={`ag-user-name-${u.id}`}>{u.name || "—"}</span>
                        )}
                      </td>
                      <td className="text-xs">
                        {editingId === u.id ? (
                          <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                 className="admin-input" />
                        ) : (
                          <span data-testid={`ag-user-email-${u.id}`}>{u.email}</span>
                        )}
                      </td>
                      <td>
                        {u.active === false ? (
                          <span className="text-xs label-uppercase text-[#B3261E] border border-[#B3261E] px-2 py-0.5" data-testid={`ag-user-status-${u.id}`}>Disattivato</span>
                        ) : (
                          <span className="text-xs label-uppercase text-[#2E7D3F] border border-[#2E7D3F] px-2 py-0.5" data-testid={`ag-user-status-${u.id}`}>Attivo</span>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap space-x-3">
                        {editingId === u.id ? (
                          <>
                            <button onClick={saveEdit} data-testid={`ag-user-save-${u.id}`} className="text-xs label-uppercase text-[#7D2E3F]">Salva</button>
                            <button onClick={() => setEditingId(null)} className="text-xs label-uppercase text-[#5C5657]">Annulla</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => impersonate(u)}
                                    data-testid={`ag-user-view-as-${u.id}`}
                                    title="Impersona questo agente (sessione QA, nessun impatto sulla password)"
                                    disabled={u.active === false}
                                    className={`text-xs label-uppercase ${u.active === false ? "text-[#C7C1B7] cursor-not-allowed" : "text-[#245A2E]"}`}>
                              Accedi come
                            </button>
                            <button onClick={() => startEdit(u)} data-testid={`ag-user-edit-${u.id}`} className="text-xs label-uppercase text-[#7D2E3F]">Modifica</button>
                            <button onClick={() => { setPwFor({ id: u.id, email: u.email }); setPwValue(""); }}
                                    data-testid={`ag-user-pw-${u.id}`} className="text-xs label-uppercase text-[#3A3D42]">Password</button>
                            <button onClick={() => toggleActive(u)} data-testid={`ag-user-toggle-${u.id}`}
                                    className="text-xs label-uppercase text-[#91590F]">
                              {u.active === false ? "Riattiva" : "Disattiva"}
                            </button>
                            <button onClick={() => del(u)} data-testid={`ag-user-delete-${u.id}`} className="text-xs label-uppercase text-[#B3261E]">Rimuovi</button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan="4" className="text-center py-6 text-[#5C5657]">Nessun agente. Aggiungine uno qui sotto.</td></tr>
                  )}
                </tbody>
              </table>

              {/* Password reset panel */}
              {pwFor && (
                <div className="mt-6 border border-[#E6E4E0] bg-[#FDFDFC] p-4" data-testid="ag-user-pw-panel">
                  <div className="label-uppercase text-[#7D2E3F] mb-2">Nuova password per {pwFor.email}</div>
                  <div className="flex items-center gap-3">
                    <input type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)}
                           placeholder="Almeno 8 caratteri" autoFocus
                           data-testid="ag-user-pw-input" className="admin-input flex-1" />
                    <button onClick={resetPassword} data-testid="ag-user-pw-confirm" className="btn-primary text-xs">Reimposta</button>
                    <button onClick={() => { setPwFor(null); setPwValue(""); }} className="text-xs label-uppercase text-[#5C5657]">Annulla</button>
                  </div>
                  <div className="text-[11px] text-[#8A8484] mt-2">Comunica la nuova password all&apos;agente tramite un canale sicuro. L&apos;azione è registrata nell&apos;audit log.</div>
                </div>
              )}

              {/* Create new user */}
              <div className="mt-8 border-t border-[#E6E4E0] pt-6">
                {!creating ? (
                  <button onClick={() => setCreating(true)} data-testid="ag-user-add-btn" className="text-xs label-uppercase border border-[#7D2E3F] text-[#7D2E3F] px-4 py-2 hover:bg-[#FDFDFC]">
                    + Aggiungi agente
                  </button>
                ) : (
                  <div className="border border-[#E6E4E0] bg-[#FDFDFC] p-4">
                    <div className="label-uppercase text-[#7D2E3F] mb-3">Nuovo agente</div>
                    <div className="grid grid-cols-3 gap-3">
                      <input placeholder="Nome e cognome" value={form.name} data-testid="ag-user-new-name"
                             onChange={(e) => setForm({ ...form, name: e.target.value })} className="admin-input" />
                      <input type="email" placeholder="Email di login" value={form.email} data-testid="ag-user-new-email"
                             onChange={(e) => setForm({ ...form, email: e.target.value })} className="admin-input" />
                      <input type="text" placeholder="Password iniziale (min 8)" value={form.password} data-testid="ag-user-new-pw"
                             onChange={(e) => setForm({ ...form, password: e.target.value })} className="admin-input" />
                    </div>
                    <div className="flex justify-end gap-3 mt-3">
                      <button onClick={() => { setCreating(false); setForm(emptyForm); }} className="text-xs label-uppercase text-[#5C5657]">Annulla</button>
                      <button onClick={create} data-testid="ag-user-new-confirm" className="btn-primary text-xs">Crea agente</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
